import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { FlyTableColumn, SortDirection } from '@fly/shared';

const SORT_COLUMN_KEY = 'sort';
const SORT_DIRECTION_KEY = 'direction';

export function useTable<T extends Record<string, any>>({
  columns,
  data,
}: {
  columns: FlyTableColumn<T>[];
  data: T[];
}) {
  const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortColumn = searchParams.get(SORT_COLUMN_KEY);
  const sortDirection = searchParams.get(SORT_DIRECTION_KEY);

  const sortedData = useMemo(() => {
    const sortFn = columns.find((col) => col.field === sortColumn)?.sort;

    if (
      !sortFn ||
      !sortDirection ||
      !Object.values(SortDirection).includes(sortDirection as SortDirection)
    ) {
      return data;
    }

    return structuredClone(data).sort((a, b) =>
      sortFn(a, b, sortDirection as SortDirection)
    );
  }, [data, columns, sortColumn, sortDirection]);

  const setSortColumn = useCallback(
    (column: string | null) => {
      setSearchParams(
        (params) => {
          if (column === null) {
            params.delete(SORT_COLUMN_KEY);
            params.delete(SORT_DIRECTION_KEY);
          } else {
            params.set(SORT_COLUMN_KEY, column ?? '');
          }
          return params;
        },
        { replace: true, state }
      );
    },
    [setSearchParams, state]
  );

  const setSortDirection = useCallback(
    (direction: SortDirection) => {
      setSearchParams(
        (params) => {
          if (direction === SortDirection.NONE) {
            params.delete(SORT_DIRECTION_KEY);
            params.delete(SORT_COLUMN_KEY);
          } else {
            params.set(SORT_DIRECTION_KEY, direction);
          }
          return params;
        },
        { replace: true, state }
      );
    },
    [setSearchParams, state]
  );

  const onSort = useCallback(
    (column: string | undefined) => {
      setSortColumn(column ?? null);

      if (column !== sortColumn) {
        setSortDirection(SortDirection.DESC);
        return;
      }

      switch (sortDirection) {
        case SortDirection.DESC:
          setSortDirection(SortDirection.ASC);
          break;
        case SortDirection.ASC:
          setSortColumn(null);
          setSortDirection(SortDirection.NONE);
          break;
        default:
          setSortDirection(SortDirection.DESC);
          break;
      }
    },
    [sortColumn, sortDirection, setSortColumn, setSortDirection]
  );

  return {
    sortedData,
    onSort,
    sortColumn,
    sortDirection,
  };
}
